import { FieldPath, FieldValues, UseFormReturn } from 'react-hook-form';
import { TextField, TextFieldProps } from '@mui/material';

type TextInputProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = {
  form: UseFormReturn<TFieldValues>;
  name: TName;
  shrink?: boolean;
} & Omit<
  TextFieldProps,
  'name' | 'value' | 'onBlur' | 'ref' | 'error' | 'helperText'
>;

export function TextInput<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  form,
  name,
  shrink: forceShrink,
  ...rest
}: TextInputProps<TFieldValues, TName>) {
  const { error } = form.getFieldState(name, form.formState);
  const value = form.watch(name);
  // prevents label from being hidden when the field is empty
  // see https://github.com/react-hook-form/react-hook-form/issues/220
  const shrink =
    forceShrink ?? (value !== undefined && value !== null && value !== '');

  const { onChange, ref, onBlur, name: fieldName } = form.register(name);

  return (
    <TextField
      {...rest}
      name={fieldName}
      error={!!error}
      helperText={error?.message}
      onChange={(event) => {
        onChange(event);
        rest.onChange?.(event);
      }}
      inputRef={ref}
      onBlur={onBlur}
      slotProps={{
        inputLabel: { shrink },
        ...(rest.slotProps ?? {}),
      }}
    />
  );
}
